import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SpinnerService } from 'src/app/services/shared/spinner.service';
import { MweConstants } from 'src/app/constants/mwe-constants.service';
import { EndCustomerService } from 'src/app/services/end-customer.service';
import Swal from 'sweetalert2';
import { SweetAlertService } from 'src/app/services/sweetalert.service';

@Component({
  selector: 'app-carousel-images',
  templateUrl: './carousel-images.component.html',
  styleUrls: ['./carousel-images.component.scss']
})
export class CarouselImagesComponent implements OnInit {

  @Input() carouselForm!: FormGroup;
  @Input() endCustomerData: any;
  @Output() onUpdate: EventEmitter<any> = new EventEmitter();

  showEditForm: boolean = false;
  data: any[] = [];
  readonly maxImageCount = 20;
  readonly maxSizeInMB = 10;
  readonly allowedFileTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  photoLimitExceeded = false;
  fileSizeExceeded = false;
  invalidFileType = false;

  constructor(
    private endCustomerService: EndCustomerService,
    private fb: FormBuilder, 
    private spinnerService: SpinnerService,
    private sweetAlertService: SweetAlertService
  ) {}

  ngOnInit(): void {
    this.data = [...(this.carouselForm.get('carouselImages')?.value || [])];
    this.initializeValidators();
  }

  private initializeValidators() {
    const carouselControl = this.carouselForm.get('carouselImages');
    carouselControl?.setValidators([Validators.maxLength(this.maxImageCount)]);
    carouselControl?.updateValueAndValidity();
  }

  get carouselImages(): FormArray {
    return this.carouselForm.get('carouselImages') as FormArray;
  }
  uploadCarousel(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.resetValidationErrors();
    if (this.carouselImages.length >= this.maxImageCount) {
      this.photoLimitExceeded = true;
      return;
    }
    if (!this.validateFile(file, input)) return;

    this.spinnerService.showSpinner();
    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('destinationKey', String(Math.floor(Math.random() * 10) + 1));

    this.endCustomerService.executePostRequestForAuth(MweConstants.uploadFile, uploadData)
      .subscribe({
        next: (data) => this.handleUploadSuccess(data, file, input),
        error: (error) => this.handleUploadError(error, input),
        complete: () => this.spinnerService.hideSpinner()
      });
  }

  private validateFile(file: File, input: HTMLInputElement): boolean {
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > this.maxSizeInMB) {
        this.fileSizeExceeded = true;
        this.sweetAlertService.showError 
          ? this.sweetAlertService.showError('File Size Exceeded', 'File should not exceed 10MB.')
          : Swal.fire({
              icon: 'error',
              title: 'File Size Exceeded',
              text: 'File should not exceed 10MB.'
            });
        this.resetInput(input);
        return false;
    }
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
        this.invalidFileType = true;
        this.sweetAlertService.showError 
          ? this.sweetAlertService.showError('Invalid File Type', 'Only JPEG, JPG, and PNG files are allowed.')
          : Swal.fire({
              icon: 'error',
              title: 'Invalid File Type',
              text: 'Only JPEG, JPG, and PNG files are allowed.'
            });
        this.resetInput(input);
        return false;
    }
    return true;
}

  private handleUploadSuccess(data: any, file: File, input: HTMLInputElement): void {
    if (data?.data) {
      this.carouselImages.push(this.fb.group({
        carouselImages: new FormControl(data.data),
        fileName: new FormControl(file.name)
      }));
      this.refresh();
    }
    this.resetInput(input);
  }

  private handleUploadError(error: any, input: HTMLInputElement): void {
    console.error('Error uploading file:', error);
    Swal.fire({
      icon: 'error',
      title: 'Upload Failed',
      text: 'An error occurred while uploading the file.'
    });
    this.resetInput(input);
  }

  private resetInput(input: HTMLInputElement): void {
    input.value = '';
  }
  private resetValidationErrors(): void {
    this.photoLimitExceeded = false;
    this.fileSizeExceeded = false;
    this.invalidFileType = false;
  }

  addPhoto(): void {
    this.showEditForm = true;
  }

  cancel(): void {
    this.carouselImages.clear();
    this.showEditForm = false;
    this.refresh();
  }

  update(): void {
    this.onUpdate.emit(70);
    this.refresh();
  }

  clearPhoto(index: number): void {
    this.carouselImages.removeAt(index);
    this.refresh();
    this.photoLimitExceeded = false;
  }

  refresh(): void {
    this.showEditForm = false;
    this.data = [...this.carouselImages.value];
  }
}
